import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  FaArrowsAlt,
  FaEdit,
  FaImage,
  FaRedo,
  FaRupeeSign,
  FaSave,
  FaSearch,
  FaUser,
} from 'react-icons/fa';
import API_BASE_URL, {
  getAdminAuthHeaders,
  handleAdminAuthError,
  isAdminAuthenticated,
} from '../api';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

const getAssetBaseUrl = () => API_BASE_URL.replace('/v1/api', '');

const getRoomImageUrl = (image) => {
  if (!image) return '';
  if (/^https?:\/\//.test(image)) return image;
  return `${getAssetBaseUrl()}${image}`;
};

const formatPrice = (price) => Number(price || 0).toLocaleString('en-IN');

const validatePrice = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
};

const Rates = () => {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [draftPrices, setDraftPrices] = useState({});
  const [dirtyPrices, setDirtyPrices] = useState({});
  const [savingPrice, setSavingPrice] = useState({});
  const [uploading, setUploading] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const fetchRooms = async () => {
    if (!isAdminAuthenticated()) {
      navigate('/login', { replace: true });
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await axios.get(`${API_BASE_URL}/rooms`, {
        headers: { 'Cache-Control': 'no-cache' },
        params: { _: Date.now() },
      });
      const list = response.data || [];
      setRooms(list);
      setDraftPrices(
        list.reduce((acc, room) => {
          acc[room.id] = String(room.price);
          return acc;
        }, {})
      );
      setDirtyPrices({});
    } catch (fetchError) {
      if (handleAdminAuthError(fetchError, navigate)) return;
      setError(fetchError.response?.data?.message || 'Failed to load room rates.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRooms = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rooms;
    return rooms.filter((room) =>
      [room.id, room.name, room.maxPerson, room.size]
        .filter((value) => value != null)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [rooms, search]);

  const averageRate = useMemo(() => {
    if (rooms.length === 0) return 0;
    const total = rooms.reduce((sum, room) => sum + Number(room.price || 0), 0);
    return Math.round(total / rooms.length);
  }, [rooms]);

  const dirtyCount = Object.keys(dirtyPrices).length;

  const handlePriceChange = (room, value) => {
    if (!/^\d*\.?\d{0,2}$/.test(value)) return;

    setDraftPrices((prev) => ({ ...prev, [room.id]: value }));
    setDirtyPrices((prev) => {
      const next = { ...prev };
      if (value === String(room.price)) {
        delete next[room.id];
      } else {
        next[room.id] = true;
      }
      return next;
    });
    setMessage('');
  };

  const discardPrice = (room) => {
    setDraftPrices((prev) => ({ ...prev, [room.id]: String(room.price) }));
    setDirtyPrices((prev) => {
      const next = { ...prev };
      delete next[room.id];
      return next;
    });
  };

  const savePrice = async (room) => {
    if (!isAdminAuthenticated()) {
      setError('Please login again before updating room rates.');
      navigate('/login', { replace: true });
      return;
    }

    const draftPrice = draftPrices[room.id];
    if (!validatePrice(draftPrice)) {
      setError('Price must be a positive number.');
      return;
    }

    setSavingPrice((prev) => ({ ...prev, [room.id]: true }));
    setError('');
    setMessage('');

    try {
      const response = await axios.put(
        `${API_BASE_URL}/rooms/${room.id}/price`,
        { price: Number(draftPrice) },
        { headers: getAdminAuthHeaders() }
      );

      const updatedRoom = response.data.room;
      setRooms((prevRooms) =>
        prevRooms.map((item) => (item.id === room.id ? updatedRoom : item))
      );
      setDraftPrices((prev) => ({ ...prev, [room.id]: String(updatedRoom.price) }));
      setDirtyPrices((prev) => {
        const next = { ...prev };
        delete next[room.id];
        return next;
      });
      setMessage(`${room.name} rate updated.`);
    } catch (saveError) {
      if (handleAdminAuthError(saveError, navigate)) return;
      setError(saveError.response?.data?.message || 'Failed to update room price.');
    } finally {
      setSavingPrice((prev) => ({ ...prev, [room.id]: false }));
    }
  };

  const handleImageChange = async (room, file) => {
    if (!file) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setError('Please upload a JPG, PNG, or WEBP image.');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      setError('Image must be 5MB or smaller.');
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    setUploading((prev) => ({ ...prev, [room.id]: true }));
    setError('');
    setMessage('');

    try {
      const response = await axios.put(`${API_BASE_URL}/rooms/${room.id}/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...getAdminAuthHeaders(),
        },
      });

      setRooms((prevRooms) =>
        prevRooms.map((item) => (item.id === room.id ? response.data.room : item))
      );
      setMessage(`${room.name} image updated.`);
    } catch (uploadError) {
      if (handleAdminAuthError(uploadError, navigate)) return;
      setError(uploadError.response?.data?.message || 'Failed to upload room image.');
    } finally {
      setUploading((prev) => ({ ...prev, [room.id]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 p-4 text-slate-900 md:p-6">
      <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-red-600">
            Rate Manager
          </p>
          <h1 className="mt-1 text-3xl font-bold">Manage Room Rates</h1>
          <p className="mt-1 text-sm text-slate-600">
            Update room prices and replace room photos used by the booking pages.
          </p>
        </div>

        <div className="relative w-full xl:w-96">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search room, ID, max guests..."
            className="h-11 w-full rounded-md border border-slate-300 bg-white pl-10 pr-3 text-sm outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100"
          />
        </div>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Rooms</p>
          <p className="mt-1 text-3xl font-bold">{rooms.length}</p>
        </div>
        <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Average Rate</p>
          <p className="mt-1 text-3xl font-bold">Rs. {formatPrice(averageRate)}</p>
        </div>
        <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Unsaved Prices</p>
          <p className="mt-1 text-3xl font-bold">{dirtyCount}</p>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-2">
        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}
        {message && (
          <p className="rounded-md bg-green-50 px-3 py-2 text-sm font-medium text-green-700">
            {message}
          </p>
        )}
      </div>

      {loading ? (
        <div className="rounded-md border border-slate-200 bg-white p-10 text-center text-slate-500 shadow-sm">
          Loading room rates...
        </div>
      ) : filteredRooms.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-white p-10 text-center text-slate-500 shadow-sm">
          No rooms match your search.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
          {filteredRooms.map((room) => {
            const draftPrice = draftPrices[room.id] ?? '';
            const isDirty = Boolean(dirtyPrices[room.id]);
            const isValidPrice = validatePrice(draftPrice);
            const imageUrl = getRoomImageUrl(room.image);

            return (
              <article
                key={room.id}
                className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm"
              >
                <div className="relative aspect-[16/7] bg-slate-200">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={room.name}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-slate-400">
                      <FaImage className="mr-2" /> No image
                    </div>
                  )}

                  <label
                    htmlFor={`room-image-${room.id}`}
                    className={`absolute right-3 top-3 inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white text-slate-700 shadow hover:bg-slate-100 ${
                      uploading[room.id] ? 'pointer-events-none opacity-60' : ''
                    }`}
                    title="Replace image"
                  >
                    <FaEdit />
                  </label>
                  <input
                    id={`room-image-${room.id}`}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      handleImageChange(room, file);
                      event.target.value = '';
                    }}
                  />

                  {uploading[room.id] && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-sm font-semibold text-slate-700">
                      Uploading image...
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-bold">{room.name}</h2>
                      <p className="text-xs font-medium text-slate-500">Room ID {room.id}</p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                      Current Rs. {formatPrice(room.price)}
                    </span>
                  </div>

                  <p className="mt-3 min-h-[48px] text-sm leading-6 text-slate-600">
                    {room.description || 'No description added.'}
                  </p>

                  <div className="mt-4 flex flex-wrap gap-3 text-sm text-slate-600">
                    <span className="inline-flex items-center gap-2 rounded-md bg-slate-100 px-3 py-2">
                      <FaArrowsAlt /> {room.size || 'N/A'} SF
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-md bg-slate-100 px-3 py-2">
                      <FaUser /> Max {room.maxPerson || 'N/A'}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-[1fr_auto_auto] gap-2">
                    <div className="relative">
                      <FaRupeeSign className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        inputMode="decimal"
                        value={draftPrice}
                        onChange={(event) => handlePriceChange(room, event.target.value)}
                        className={`h-11 w-full rounded-md border pl-9 pr-3 text-center font-semibold outline-none transition ${
                          isDirty
                            ? 'border-amber-400 bg-amber-50 text-amber-900'
                            : 'border-slate-300 bg-white text-slate-900'
                        } focus:border-red-500 focus:ring-2 focus:ring-red-100`}
                        aria-label={`${room.name} room price`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => discardPrice(room)}
                      disabled={!isDirty || savingPrice[room.id]}
                      className="inline-flex h-11 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <FaRedo /> Reset
                    </button>
                    <button
                      type="button"
                      onClick={() => savePrice(room)}
                      disabled={!isDirty || !isValidPrice || savingPrice[room.id]}
                      className="inline-flex h-11 items-center gap-2 rounded-md bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <FaSave /> {savingPrice[room.id] ? 'Saving' : 'Save'}
                    </button>
                  </div>

                  {!isValidPrice && draftPrice && (
                    <p className="mt-2 text-xs font-medium text-red-600">
                      Enter a positive price.
                    </p>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Rates;
